// One retry classification for every authoring error (achievement authoring
// spec 7.3). The server sends these codes with sendError; the studio upload
// queue, the in-game panel and the admin screen classify every response here.

/**
 * The account a studio mutation means (spec 7.1): the site's cookie is shared by every tab, so another
 * tab's sign-in can change who a request acts as. Compared as text with the session's user id.
 */
export const AUTHORING_ACCOUNT_HEADER = 'x-authoring-account';

export const AUTHORING_RETRY_CLASSES = Object.freeze([
  'terminal', 'needs_input', 'reauth', 'stale', 'completed', 'dependency', 'retryable',
]);

const entry = (status, retryClass) => Object.freeze({ status, retryClass });

/** Every spec 7.3 catalogue code with its HTTP status and retry class. */
export const AUTHORING_ERROR_CODES = Object.freeze({
  bad_param: entry(400, 'terminal'),
  invalid_version: entry(400, 'terminal'),
  gate_required: entry(400, 'terminal'),
  bad_role: entry(400, 'terminal'),
  rom_mismatch: entry(400, 'terminal'),
  leaderboard_choice_required: entry(400, 'needs_input'),
  confirmation_mismatch: entry(400, 'needs_input'),
  admin_step_up_required: entry(403, 'reauth'),
  not_found: entry(404, 'terminal'),
  conflict: entry(409, 'stale'),
  not_ready: entry(409, 'terminal'),
  static_score_exists: entry(409, 'terminal'),
  score_item_exists: entry(409, 'terminal'),
  cache_capacity: entry(409, 'terminal'),
  item_was_published: entry(409, 'terminal'),
  not_published: entry(409, 'terminal'),
  mark_limit: entry(409, 'terminal'),
  test_limit: entry(409, 'terminal'),
  quota_exceeded: entry(409, 'terminal'),
  evidence_exists: entry(409, 'terminal'),
  evidence_mismatch: entry(409, 'terminal'),
  evidence_dependency: entry(409, 'dependency'),
  stale_evidence: entry(409, 'stale'),
  verdict_already_set: entry(409, 'completed'),
  account_changed: entry(409, 'reauth'),
  gone: entry(410, 'terminal'),
  length_required: entry(411, 'terminal'),
  evidence_too_large: entry(413, 'terminal'),
  rate_limited: entry(429, 'retryable'),
  uploads_busy: entry(429, 'retryable'),
  authored_cache_unavailable: entry(503, 'retryable'),
});

/**
 * Classify an authoring response. Status 0, or no status at all, is a network
 * error. A known code counts only with its catalogued status; anything else
 * falls back on the status: 5xx, 408 and 429 are retryable, 401 and 403 need a
 * fresh sign-in (reauth), and any other 4xx is terminal. A terminal file
 * response is resolved by cancel or abandon (spec 8.9), so a transient or
 * authentication refusal is never terminal. A 5xx whose body says `commit: 'unknown'` may have
 * committed, so it is `stale` (reload, then decide), never `retryable`: it must not be resent blindly.
 * Returns null for a response that is not an error (status below 400).
 */
export function classifyAuthoringResponse({ status, code, commit } = {}) {
  if (!Number.isInteger(status) || status <= 0) return 'retryable';
  if (status < 400) return null;
  if (status >= 500 && commit === 'unknown') return 'stale';
  if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(AUTHORING_ERROR_CODES, code)) {
    const known = AUTHORING_ERROR_CODES[code];
    if (known.status === status) return known.retryClass;
  }
  if (status >= 500 || status === 408 || status === 429) return 'retryable';
  if (status === 401 || status === 403) return 'reauth';
  return 'terminal';
}
